import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { $isLinkNode, LinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
} from "@lexical/list";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
} from "lexical";
import { useEffect } from "react";

/**
 * Email body editor on Lexical, as the Inbox compose (user, 2026-10-29: the
 * Quill body still read double spaced in Gmail; Lexical is what fixed it in
 * the Inbox). Send its HTML through emailBodyHtml() (utils/emailBodyHtml).
 *
 * html     the body to show; it is loaded again whenever loadKey changes, so
 *          typing never reloads it
 * onChange(html)
 * editorRef  optional ref; gets { insertText(text) } -- the Settings ->
 *          Locations Variables buttons type into the body with it
 */

const THEME = {
  paragraph: "email-body-editor-p",
  link: "email-body-editor-link",
};

const LoadHtmlPlugin = ({ html, loadKey }) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      if (html) {
        const dom = new DOMParser().parseFromString(html, "text/html");
        $generateNodesFromDOM(editor, dom).forEach((node) => {
          if ($isElementNode(node)) root.append(node);
          else root.append($createParagraphNode().append(node));
        });
      }
      if (root.getChildrenSize() === 0) root.append($createParagraphNode());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, loadKey]);
  return null;
};

// Puts insertText(text) on editorRef: at the cursor, or at the end when the
// editor has no selection.
const InsertTextPlugin = ({ editorRef }) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (!editorRef) return;
    editorRef.current = {
      insertText: (text) => {
        editor.update(() => {
          let selection = $getSelection();
          if (!$isRangeSelection(selection)) selection = $getRoot().selectEnd();
          selection.insertText(text);
        });
        editor.focus();
      },
    };
    return () => {
      editorRef.current = null;
    };
  }, [editor, editorRef]);
  return null;
};

const Toolbar = () => {
  const [editor] = useLexicalComposerContext();
  const button = (icon, title, onClick) => (
    <button
      type="button"
      className="btn btn-sm btn-light"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      <i className={`mdi ${icon}`}></i>
    </button>
  );
  const toggleLink = () => {
    let isLink = false;
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      const node = selection.anchor.getNode();
      isLink = $isLinkNode(node) || $isLinkNode(node.getParent());
    });
    if (isLink) return editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    const url = window.prompt("Link address");
    if (url) editor.dispatchCommand(TOGGLE_LINK_COMMAND, url.trim());
  };
  return (
    <div className="d-flex gap-1 border-bottom p-1 bg-light">
      {button("mdi-format-bold", "Bold", () =>
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold"),
      )}
      {button("mdi-format-italic", "Italic", () =>
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic"),
      )}
      {button("mdi-format-underline", "Underline", () =>
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline"),
      )}
      {button("mdi-format-list-bulleted", "Bullet list", () =>
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
      )}
      {button("mdi-link-variant", "Link", toggleLink)}
    </div>
  );
};

export default function EmailBodyEditor({
  html,
  loadKey,
  onChange,
  editorRef,
  minHeight = 220,
}) {
  const config = {
    namespace: "email-body-editor",
    theme: THEME,
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
    onError: (error) => console.error("Email body editor", error),
  };
  return (
    <div className="border rounded bg-white">
      <style>{`
        .email-body-editor-p { margin: 0; padding: 0; line-height: 1.4; }
        .email-body-editor-link { color: #1d4ed8; text-decoration: underline; }
      `}</style>
      <LexicalComposer initialConfig={config}>
        <Toolbar />
        <div className="position-relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                aria-label="Email body"
                className="p-2"
                style={{ minHeight, outline: "none" }}
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <LoadHtmlPlugin html={html} loadKey={loadKey} />
        <InsertTextPlugin editorRef={editorRef} />
        <OnChangePlugin
          ignoreSelectionChange
          onChange={(state, editor) =>
            state.read(() => onChange?.($generateHtmlFromNodes(editor, null)))
          }
        />
      </LexicalComposer>
    </div>
  );
}
